import { useWallet } from '@txnlab/use-wallet-react';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import algosdk from 'algosdk';
import { getAlgodConfigFromViteEnvironment } from '../utils/network/getAlgoClientConfigs';
import AlgoMark from './AlgoMark';
import {
  APP_SHARE,
  CREATOR_SHARE,
  LSIG_ADDRESS,
  LSIG_PROGRAM_B64,
  LSIG_SHARE,
  SPIN_APP_ID,
} from '../constants/spin';

const SWEEP = 360 / 5;

const WHEEL = [
  { micro: 0, label: '0' },
  { micro: 100_000, label: '0.1' },
  { micro: 200_000, label: '0.2' },
  { micro: 500_000, label: '0.5' },
  { micro: 1_000_000, label: '1' },
].map((slice, index) => ({
  ...slice,
  start: index * SWEEP,
  sweep: SWEEP,
  mid: index * SWEEP + SWEEP / 2,
  jackpot: slice.micro === 1_000_000,
}));

function polar(cx: number, cy: number, radius: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)];
}

function wedge(start: number, sweep: number) {
  const radius = 90;
  const [x1, y1] = polar(100, 100, radius, start);
  const [x2, y2] = polar(100, 100, radius, start + sweep);
  const large = sweep > 180 ? 1 : 0;
  return `M 100 100 L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z`;
}

function PixelLoader() {
  return (
    <div className="px-loader px-body-center spin-status-loader" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
  );
}

function prizeLine(micro: number) {
  if (micro >= 1_000_000) return '1 ALGO !';
  if (micro >= 500_000) return '0.5 ALGO !';
  if (micro >= 200_000) return '0.2 ALGO !';
  if (micro >= 100_000) return '0.1 ALGO !';
  return 'TRY AGAIN !';
}

function sliceFor(micro: number) {
  return WHEEL.find((slice) => slice.micro === micro) ?? WHEEL[0];
}

function uint64(bytes: Uint8Array, offset: number): number {
  let value = 0n;
  for (let i = 0; i < 8; i += 1) value = (value << 8n) + BigInt(bytes[offset + i]);
  return Number(value);
}

function toBytes(value: unknown): Uint8Array {
  if (value instanceof Uint8Array) return value;
  if (typeof value === 'string') return Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
  return new Uint8Array(value as ArrayBuffer);
}

function logicSig() {
  const program = Uint8Array.from(atob(LSIG_PROGRAM_B64), (char) => char.charCodeAt(0));
  return new algosdk.LogicSigAccount(program);
}

interface SpinModalProps {
  open: boolean;
  onClose: () => void;
}

const SpinModal = ({ open, onClose }: SpinModalProps) => {
  const { activeAddress, transactionSigner } = useWallet();
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [locked, setLocked] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [mbr, setMbr] = useState<number | null>(null);
  const wheelRef = useRef<HTMLDivElement | null>(null);

  const readWheelAngle = () => {
    const el = wheelRef.current;
    if (!el) return 0;
    const transform = getComputedStyle(el).transform;
    if (!transform || transform === 'none') return 0;
    const matrix = new DOMMatrix(transform);
    return ((Math.atan2(matrix.b, matrix.a) * 180) / Math.PI + 360) % 360;
  };

  const spinWheel = () => {
    const el = wheelRef.current;
    if (!el) return;
    el.getAnimations().forEach((anim) => anim.cancel());
    el.style.transition = 'none';
    el.style.animation = 'spin-wheel-roll 0.45s linear infinite';
  };

  const freezeWheel = () => {
    const el = wheelRef.current;
    if (!el) return;
    const current = readWheelAngle();
    el.style.animation = 'none';
    el.style.transition = 'none';
    el.style.transform = `rotate(${current}deg)`;
  };

  const landWheel = (micro: number) => new Promise<void>((resolve) => {
    const el = wheelRef.current;
    if (!el) {
      resolve();
      return;
    }
    const current = readWheelAngle();
    const mid = sliceFor(micro).mid;
    const desired = (360 - mid) % 360;
    const delta = (desired - (current % 360) + 360) % 360;
    const next = current + 360 * 5 + delta;
    el.style.transition = 'none';
    el.style.transform = `rotate(${current}deg)`;
    el.style.animation = 'none';
    el.getAnimations().forEach((anim) => anim.cancel());
    el.style.transform = `rotate(${current}deg)`;
    const anim = el.animate(
      [
        { transform: `rotate(${current}deg)` },
        { transform: `rotate(${next}deg)` },
      ],
      { duration: 4800, easing: 'cubic-bezier(0.15, 0.28, 0.2, 1)', fill: 'forwards' },
    );
    anim.onfinish = () => {
      el.style.transform = `rotate(${desired}deg)`;
      anim.cancel();
      resolve();
    };
  });

  const algodClient = useMemo(() => {
    const cfg = getAlgodConfigFromViteEnvironment();
    return new algosdk.Algodv2(String(cfg.token), cfg.server, cfg.port);
  }, []);

  useEffect(() => {
    if (!open || !activeAddress) {
      setLocked(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const name = algosdk.decodeAddress(activeAddress).publicKey;
        const box = await algodClient.getApplicationBoxByName(SPIN_APP_ID, name).do();
        const value = toBytes((box as any).value);
        if (!cancelled) setLocked(value[0] === 1);
      } catch {
        if (!cancelled) setLocked(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, activeAddress, algodClient]);

  const refreshBalance = async () => {
    try {
      const info = await algodClient.accountInformation(algosdk.getApplicationAddress(SPIN_APP_ID)).do();
      setBalance(Number(info.amount) / 1_000_000);
      setMbr(Number((info as any).minBalance ?? (info as any)['min-balance'] ?? 0) / 1_000_000);
    } catch {
      setBalance(null);
      setMbr(null);
    }
  };

  useEffect(() => {
    if (!open) return;
    refreshBalance();
    const el = wheelRef.current;
    if (!el || el.style.animation) return;
    el.style.transform = `rotate(${(360 - WHEEL[0].mid) % 360}deg)`;
  }, [open]);

  const readPending = async (address: string): Promise<number> => {
    try {
      const name = algosdk.decodeAddress(address).publicKey;
      const box = await algodClient.getApplicationBoxByName(SPIN_APP_ID, name).do();
      const value = toBytes((box as any).value);
      return uint64(value, 9);
    } catch {
      return 0;
    }
  };

  const waitTx = async (txid: string) => {
    for (let i = 0; i < 40; i += 1) {
      const info = await algodClient.pendingTransactionInformation(txid).do();
      const confirmed = (info as any).confirmedRound ?? (info as any)['confirmed-round'];
      if (confirmed) return info as any;
      const poolError = (info as any).poolError || (info as any)['pool-error'];
      if (poolError) throw new Error(String(poolError));
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    throw new Error('Transaction not confirmed.');
  };

  const handleSpin = async () => {
    if (!activeAddress || !transactionSigner) {
      enqueueSnackbar('Please connect your wallet', { variant: 'error' });
      return;
    }
    setBusy(true);
    let rolling = false;
    try {
      const params = await algodClient.getTransactionParams().do();
      if (!String((params as any).genesisID ?? (params as any).genesisId ?? '').toLowerCase().startsWith('mainnet')) {
        enqueueSnackbar('Please switch to MainNet in your wallet', { variant: 'error' });
        return;
      }
      spinWheel();
      rolling = true;

      const playerKey = algosdk.decodeAddress(activeAddress).publicKey;
      let seedRound = await readPending(activeAddress);

      if (!seedRound) {
        const appAddress = algosdk.getApplicationAddress(SPIN_APP_ID);
        const appInfo = await algodClient.getApplicationByID(SPIN_APP_ID).do();
        const creator = String((appInfo as any).params.creator);
        const zeroFee = { ...params, flatFee: true, fee: 0 };
        const feeParams = { ...params, flatFee: true, fee: 5000 };
        const payCreator = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
          sender: activeAddress,
          receiver: creator,
          amount: CREATOR_SHARE,
          suggestedParams: zeroFee,
        });
        const payLsig = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
          sender: activeAddress,
          receiver: LSIG_ADDRESS,
          amount: LSIG_SHARE,
          suggestedParams: zeroFee,
        });
        const payApp = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
          sender: activeAddress,
          receiver: appAddress,
          amount: APP_SHARE,
          suggestedParams: zeroFee,
        });
        const feeTxn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
          sender: LSIG_ADDRESS,
          receiver: LSIG_ADDRESS,
          amount: 0,
          suggestedParams: feeParams,
        });
        const call = algosdk.makeApplicationNoOpTxnFromObject({
          sender: activeAddress,
          appIndex: SPIN_APP_ID,
          appArgs: [new TextEncoder().encode('spin')],
          boxes: [{ appIndex: SPIN_APP_ID, name: playerKey }],
          suggestedParams: zeroFee,
        });
        const group = [payCreator, payLsig, payApp, feeTxn, call];
        algosdk.assignGroupID(group);
        const signed = await transactionSigner([group[0], group[1], group[2], group[3], group[4]], [0, 1, 2, 4]);
        const lsigSigned = (algosdk as any).signLogicSigTransactionObject(group[3], logicSig());
        const lsigBlob = lsigSigned.blob || lsigSigned;
        await algodClient.sendRawTransaction([signed[0], signed[1], signed[2], lsigBlob, signed[3]]).do();
        const spun = await waitTx(group[4].txID());
        const logs = (spun.logs || spun['logs'] || []) as unknown[];
        if (logs.length) {
          const log = toBytes(logs[0]);
          seedRound = uint64(log, log.length - 8);
        } else {
          seedRound = await readPending(activeAddress);
        }
        if (!seedRound) throw new Error('Spin confirmed, but the contract wrote no result.');
      }

      let round = Number(((await algodClient.status().do()) as any).lastRound ?? 0);
      while (round <= seedRound) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        round = Number(((await algodClient.status().do()) as any).lastRound ?? 0);
      }

      const revealParams = await algodClient.getTransactionParams().do();
      const reveal = algosdk.makeApplicationNoOpTxnFromObject({
        sender: LSIG_ADDRESS,
        appIndex: SPIN_APP_ID,
        appArgs: [new TextEncoder().encode('reveal'), playerKey],
        accounts: [activeAddress],
        boxes: [{ appIndex: SPIN_APP_ID, name: playerKey }],
        suggestedParams: {
          ...revealParams,
          flatFee: true,
          fee: 2000,
          firstValid: seedRound + 1,
          lastValid: seedRound + 1001,
        },
      });
      const revealSigned = (algosdk as any).signLogicSigTransactionObject(reveal, logicSig());
      const revealBlob = revealSigned.blob || revealSigned;
      await algodClient.sendRawTransaction(revealBlob).do();
      const revealed = await waitTx(reveal.txID());
      const winLogs = (revealed.logs || revealed['logs'] || []) as unknown[];
      if (!winLogs.length) throw new Error('Reveal confirmed, but the contract wrote no result.');
      const win = toBytes(winLogs[0]);
      const prize = uint64(win, 3 + 40);
      await landWheel(prize);
      rolling = false;
      const line = prizeLine(prize);
      setStatus(line);
      refreshBalance();
      enqueueSnackbar(line, {
        variant: prize > 0 ? 'success' : 'info',
      });
    } catch (error: any) {
      if (rolling) freezeWheel();
      const message = error?.message || 'Spin failed';
      setStatus(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="px-overlay" data-modal-root="true">
      <div className="px-modal px-modal-fixed">
        <div className="px-modal-head spin-modal-head">
          <div className="spin-head-stats">
            <span className="px-btn is-on spin-balance">
              {balance === null ? '—' : balance.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')}
              <AlgoMark />
            </span>
            <div className="px-meta">
              <span className="px-meta-pair">
                <span className="px-meta-label">MBR</span>
                <span className="px-meta-amt">{mbr === null ? '—' : mbr}<AlgoMark /></span>
              </span>
            </div>
          </div>
          <button className="px-btn" type="button" onClick={onClose} disabled={busy}>X</button>
        </div>
        <div className="px-body hidden-scrollbar">
          <div className="px-body-center">
            <div className="spin-panel">
              <div className="spin-stage">
                <div className="spin-pointer" />
                <div className="spin-wheel" ref={wheelRef}>
                  <svg className="spin-svg" viewBox="0 0 200 200" aria-hidden="true">
                    {WHEEL.map((slice) => (
                      <path
                        key={slice.label}
                        d={wedge(slice.start, slice.sweep)}
                        fill={slice.jackpot ? '#8cd9ff' : 'none'}
                        stroke="#f4f0de"
                        strokeWidth="2"
                      />
                    ))}
                    {WHEEL.map((slice) => {
                      const [x, y] = polar(100, 100, 62, slice.mid);
                      return (
                        <text
                          key={`${slice.label}-label`}
                          x={x}
                          y={y}
                          fill={slice.jackpot ? '#001133' : '#f4f0de'}
                          fontSize="10"
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {slice.label}
                        </text>
                      );
                    })}
                  </svg>
                </div>
              </div>
              <div className="px-status">{locked ? 'Address locked' : busy ? <PixelLoader /> : status}</div>
              <button className="px-btn" type="button" disabled={busy || locked} onClick={handleSpin}>
                {busy ? 'WAIT...' : 'SPIN'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SpinModal;
